/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { createSearchParams, URLSearchParamsInit } from "react-router-dom";

import apiClient from "../../../services/api-client";
import { RootState } from "../../../store/rootReducer";
import hashObject from "../../../util/hash";
import { getProperty } from "../../azure-b2c/storage";

import {
  Quote,
  QuotesReducerAction,
  QuotesRequestCache,
  QuotesState,
  SearchQuotesPayload,
  SearchQuotesResponse,
} from "./types";

export const initialState: QuotesState = {
  quotes: {},
  quotesStatus: {},
};

export const argToHash = (query: SearchQuotesPayload) => {
  const obj = {
    a: query.startDateTime,
    b: query.endDateTime,
    c: query.salesId,
    d: query.expiringSoon,
    e: query.accountNumber,
  };
  return hashObject(obj);
};

const areQuotesEqual = (quote1: Quote, quote2: Quote) => {
  return _.isEqual(quote1, quote2);
};

const searchQuotesApi = async (payload: SearchQuotesPayload): Promise<QuotesReducerAction> => {
  const params: URLSearchParamsInit = {
    accountNumber: payload.accountNumber,
  };
  if (payload.top) {
    params.top = payload.top?.toString();
  }
  if (payload.skip) {
    params.skip = payload.skip?.toString();
  }
  if (payload.salesId) {
    params.salesId = payload.salesId?.toString();
  }
  if (payload.startDateTime) {
    params.startDateTime = payload.startDateTime?.toString();
  }
  if (payload.endDateTime) {
    params.endDateTime = payload.endDateTime?.toString();
  }
  if (payload.expiringSoon) {
    params.expiringSoon = "1";
  }

  const request = await apiClient.get<SearchQuotesResponse, SearchQuotesPayload>(
    `/quotes?${createSearchParams(params)}`
  );

  return {
    data: request.data.quotes,
    accountNumber: payload.accountNumber,
    last: true, // TODO: when we use pagination. request.data.quotes.length < MAX_ORDERS_PAGE_SIZE,
  };
};

export const getQuotes = createAsyncThunk<QuotesReducerAction, SearchQuotesPayload>(
  "quotes/search-quotes",
  async (payload) => {
    return await searchQuotesApi({ ...payload, skip: payload.skip });
  }
);

const QuoteSlice = createSlice({
  name: "quotes",
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(getQuotes.pending, (state, args) => {
      const hash = argToHash(args.meta.arg);
      state.quotesStatus[args.meta.arg.accountNumber] = {
        ...state.quotesStatus[args.meta.arg.accountNumber],
        [hash]: {
          ...state.quotesStatus[args.meta.arg.accountNumber],
          lastRequest: args.meta.arg,
          status: "pending",
          hash,
        },
      };
    });
    builder.addCase(getQuotes.fulfilled, (state, args) => {
      const hash = argToHash(args.meta.arg);
      const data = args.payload.data;
      const existing = state.quotes[args.meta.arg.accountNumber]?.[hash] ?? [];
      const intersections = _.intersectionWith(data, existing, areQuotesEqual);

      if (intersections.length > 0) {
        state.quotes[args.meta.arg.accountNumber] = {
          ...state.quotes[args.meta.arg.accountNumber],
          [hash]: [...data],
        };
      } else {
        state.quotes[args.meta.arg.accountNumber] = {
          ...state.quotes[args.meta.arg.accountNumber],
          [hash]: [...existing, ...data],
        };
      }

      state.quotesStatus[args.meta.arg.accountNumber] = {
        ...state.quotesStatus[args.meta.arg.accountNumber],
        [hash]: {
          ...state.quotesStatus[args.meta.arg.accountNumber][hash],
          status: "fulfilled",
          last: args.payload.last,
        },
      };
    });
    builder.addCase(getQuotes.rejected, (state, args) => {
      const hash = argToHash(args.meta.arg);
      state.quotesStatus[args.meta.arg.accountNumber] = {
        ...state.quotesStatus[args.meta.arg.accountNumber],
        [hash]: { lastRequest: args.meta.arg, status: "rejected", hash: hash },
      };
    });
  },
  reducers: {
    clear(state: QuotesState) {
      state.quotes = {};
      state.quotesStatus = {};
    },
  },
});

const selectState = (state: RootState) => state;

export const selectQuote = (id: string) => {
  return createSelector(selectState, (state) => {
    const property = getProperty(state);
    if (state.quotes.quotes[property as string]) {
      return state.quotes.quotes[property as string][id] ?? [];
    }
    return [];
  });
};

export const selectQuoteStatus = (id: string) => {
  return createSelector(selectState, (state): QuotesRequestCache | null => {
    const property = getProperty(state);

    if (state.quotes.quotesStatus[property as string]) {
      return state.quotes.quotesStatus[property as string][id] ?? null;
    }

    return null;
  });
};

export type { QuotesState } from "./types";
export default QuoteSlice;
